import React, { useRef, useState } from 'react';
import { connect } from "react-redux";
import * as H from 'history';
import { match } from "react-router-dom";
import { ApplicationState } from "../../store";
import { CommandBar, IContextualMenuItem } from "office-ui-fabric-react";
import { Integrations, Subscription, TenantState } from "../../store/Tenant";
import NewEntityHeaderButton from "./NewEntityHeaderButton";
import { notUndefined, toDictionaryById } from "../utils/common";
import { EntityType, IEditableExtensibleEntity } from "../../entities/common";
import { UserState } from "../../store/User";
import * as Metadata from '../../entities/Metadata';
import { PngExportConfig } from './PngExporter';
import { ICardSortOption, ICardSortState } from '../../store/views';
import EntitiesFilterTags from './EntitiesFilterTags/EntitiesFilterTags';
import { bindActionCreators } from 'redux';
import { actionCreators as PngExportActionCreators } from '../../store/PngExporterStore';
import { buildActionsMenuItem, IReportsProps, buildReportsMenuItem, buildSortByMenuItem, buildPlanningAndUtilizationMenuItem, buildReportedTimeMenuItem, buildAuditLogMenuItem } from './headerMenuItemBuilders';
import ImportProjects, { supported as ImportProjectsIntegrations } from '../import/project/ImportPanel';
import ImportResources, { supported as ImportResourcesIntegrations } from '../import/resource/ImportPanel';
import ListMenuItemSelectorBuilder from './ListMenuItemSelector';
import { ExtensionPageLocation, ExtensionInfo, findExtension } from '../../store/ExtensionStore';
import ExtensionPanel from './ExtensionPanel';
import { getActiveSubViewColumnIds } from './EntitiesScreen';
import { SearchBox, SearchValue } from './SearchBox';

export interface HeaderProps {
    entityType: EntityType;
    createEntityTypes: EntityType[];
    importEntityTypes: EntityType[];
    reportProps?: IReportsProps;
    allowImportFromFile?: boolean;
    allowExportToFile?: boolean;
};

export type EntitiesScreenRouter = {
    history: H.History;
    match: match<{
        type?: string;
        subViewId?: string;
    }>;
    location: H.Location;
};

export type EntitiesScreenSubType = {
    title: string;
    iconName: string;
    onClick: () => void;
    isActive: () => boolean;
};

export type EntitiesScreenSubTypeGroup = {
    key: string;
    title: string;
    iconName: string;
    isActive: () => boolean;
    subTypes: EntitiesScreenSubType[];
};

export interface IEntitiesScreenView<T> {
    icon: string;
    title?: string;
    subTypeGroups?: EntitiesScreenSubTypeGroup[];
    url: string;
    subViews: Metadata.ISubView[];
    render: (key: string, view: Metadata.ISubView, entities: T[]) => JSX.Element | JSX.Element[];
    onAddSubViewClick?: () => void;
    onEditSubViewClick?: (id: string) => void;
    onCopySubViewClick?: (view: Metadata.ISubView) => void;
    onRemoveSubViewClick?: (id: string) => void;
    sortBy?: ICardSortState;
    onSortChange?: (sortBy: ICardSortOption) => void;
    activeSubViewId?: string;
    onSubViewChange: (subViewId: string) => void;
    headerSideItems?: IContextualMenuItem[];
}

export type ActiveView<T> = {
    view: IEntitiesScreenView<T>;
    subView: Metadata.ISubView;
};

export type EntitiesScreenFilter = {
    activeFilter?: Metadata.IFilter<Metadata.BaseFilterValue>;
    autoFilterId: string;
    onFilterRender: (isFilterPanelOpen: boolean, toggleFilterPanel: () => void) => JSX.Element | null;
    getAttributeValue: (attrType: string, value: any) => string[];
};

export type EntitiesScreenSearch = {
    search?: SearchValue;
    onSearch: (value: SearchValue) => void;
}

const ListMenuItemSelector = ListMenuItemSelectorBuilder<Metadata.ISubView>();

type OwnProps = {
    headerProps: HeaderProps;
    router: EntitiesScreenRouter;
    active: ActiveView<IEditableExtensibleEntity>;
    filter: EntitiesScreenFilter;
    search?: EntitiesScreenSearch;
    fields: Metadata.Field[];
    isFieldFake?: (field: Metadata.Field) => boolean;
    entities: IEditableExtensibleEntity[];
    canEdit: boolean;
    canManageConfiguration: boolean;
    baseUrl: string;
    getPngExportConfig?: () => PngExportConfig;
    importFromFile?: (file: File) => void;
};

type StateProps = {
    subscription: Subscription;
    user: UserState;
    tenant: TenantState;
    integrations: Integrations;
    extensions: ExtensionInfo[];
};

type ActionProps = {
    pngExporterActions: typeof PngExportActionCreators;
};

type Props = OwnProps & StateProps & ActionProps;

const pngExportedViewTypes: string[] = [Metadata.ViewTypes.timeline as string, Metadata.ViewTypes.list as string];

const EntitiesScreenHeader = (props: Props) => {
    const { headerProps, router, subscription, integrations, user, tenant, active, filter, pngExporterActions, canEdit } = props;

    const fileUpload = useRef<HTMLInputElement>(null);
    const [entityTypeToCreate, setEntityTypeToCreate] = useState<EntityType>();
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isExtensionPanelOpen, setIsExtensionPanelOpen] = useState<ExtensionInfo>();

    const projectIntegrations = integrations.getAvailable(ImportProjectsIntegrations);
    const resourceIntegrations = integrations.getAvailable(ImportResourcesIntegrations);

    const getGetPngExportConfig = () => {
        return !!active.view.url && pngExportedViewTypes.includes(active.view.url.toLowerCase())
            ? props.getPngExportConfig
            : undefined;
    }

    const primaryItems: IContextualMenuItem[] = [
        {
            key: 'new-entity',
            onRender: () => <NewEntityHeaderButton
                entityTypes={Subscription.getCreateEntityTypes(subscription, tenant.defaultProjectSettings, headerProps.createEntityTypes)}
                currentEntityType={headerProps.entityType} />
        },
        buildPlanningAndUtilizationMenuItem({ user, subscription, headerProps, router }),
        buildReportedTimeMenuItem({ user, subscription, headerProps, router, tenant }),
        buildReportsMenuItem(headerProps.reportProps, tenant),
        buildActionsMenuItem({
            entityIds: props.entities.map(e => e.id),
            readonly: !canEdit,
            importEntityTypes: headerProps.importEntityTypes,
            currentEntityType: headerProps.entityType,
            user,
            projectIntegrations,
            resourceIntegrations,
            pngExporterActions: pngExporterActions,
            getPngExportConfig: getGetPngExportConfig(),
            onCreate: setEntityTypeToCreate,
            onCsvImport: headerProps.allowImportFromFile ? () => fileUpload.current?.click() : undefined,
            extensions: props.extensions,
            onExtensionClick: (_) => setIsExtensionPanelOpen(_),
        }),
        buildAuditLogMenuItem({ subscription, headerProps, router })
    ].filter(notUndefined);

    const activeSubViewColumnIds = React.useMemo(() => getActiveSubViewColumnIds([active.view], active.subView), [active]);

    const farItems: IContextualMenuItem[] = [
        {
            key: 'filter-tags',
            onRender: () => <EntitiesFilterTags
                getAttributeValue={filter.getAttributeValue}
                filter={filter.activeFilter}
                showRightDivider
                onTagClick={filter.activeFilter && (!filter.activeFilter.isPublic || props.canManageConfiguration)
                    ? () => setIsFilterPanelOpen(!isFilterPanelOpen)
                    : undefined}
            />
        },
        (props.search && activeSubViewColumnIds.length > 0 ? {
            key: 'search',
            onRender: () => <SearchBox
                entityType={props.headerProps.entityType}
                viewColumns={activeSubViewColumnIds}
                fieldsMap={toDictionaryById(props.fields)}
                isFieldFake={props.isFieldFake}
                value={props.search!.search}
                onSearch={props.search!.onSearch}
            />
        } : undefined),
        ...(active.view.headerSideItems ?? []),
        buildSortByMenuItem(active),
        {
            key: 'views',
            onRender: () => <ListMenuItemSelector
                itemType='View'
                activeItem={active.subView}
                items={active.view.subViews}
                onActiveItemChanged={id => props.router.history.push(`${props.baseUrl}/${active.view.url}/${id}`)}
                canManageConfiguration={props.canManageConfiguration}
                onItemAddClick={active.view.onAddSubViewClick}
                onItemEditClick={active.view.onEditSubViewClick}
                onItemRemoveClick={active.view.onRemoveSubViewClick}
                onItemCopyClick={active.view.onCopySubViewClick}
            />
        }
    ].filter(notUndefined);

    return (
        <>
            <CommandBar
                className="header-command-bar"
                style={{ width: '100%' }}
                overflowButtonProps={{ menuProps: { items: [], isBeakVisible: false } }}
                items={primaryItems}
                farItems={farItems}
            />
            {filter.onFilterRender(isFilterPanelOpen, () => setIsFilterPanelOpen(!isFilterPanelOpen))}
            {entityTypeToCreate === EntityType.Project && <ImportProjects integrations={projectIntegrations} onDismiss={() => setEntityTypeToCreate(undefined)} />}
            {entityTypeToCreate === EntityType.Resource && <ImportResources integrations={resourceIntegrations} onDismiss={() => setEntityTypeToCreate(undefined)} />}
            <input ref={fileUpload} style={{ display: "none" }} type="file" accept=".csv"
                onChange={(e) => {
                    if (e.target && e.target.files && e.target.files[0]) {
                        props.importFromFile?.(e.target.files[0]);
                    }
                    e.target.value = '';
                }}
            />
            {isExtensionPanelOpen && <ExtensionPanel
                entityType={headerProps.entityType}
                info={isExtensionPanelOpen}
                onDismiss={() => setIsExtensionPanelOpen(undefined)}
                context={{ entityType: headerProps.entityType }}
            />}
        </>
    );
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        subscription: state.tenant.subscription,
        user: state.user,
        tenant: state.tenant,
        integrations: new Integrations(state.tenant.subscription.integrations),
        extensions: findExtension(state.extensions, ownProps.headerProps.entityType, ExtensionPageLocation.Actions)
    };
}

function mergeActionCreators(dispatch: any): ActionProps {
    return {
        pngExporterActions: bindActionCreators(PngExportActionCreators, dispatch),
    };
}

export default connect(mapStateToProps, mergeActionCreators)(EntitiesScreenHeader);
